import { Link } from "react-router-dom";
import { effectivePricePerM2, type CatalogOverrides, type Promotion } from "@glastotaal/core";
import { getPromotableItem, soortSlug, soortenWithOverrides } from "@/lib/catalog";
import { formatEuro } from "@/lib/format";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { overrideField, placementSet, priceWithSale, usePromotions, type PromoMap } from "@/lib/promotions";
import { ProductRatingBadge } from "@/components/ProductRatingBadge";
import { AddToCartButton, ConfigureButton } from "@/components/ui";
import { useCart } from "@/features/cart";
import { useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";

export interface FeaturedItem {
  slug: string;
  chip: string;
  title: string;
  subtitle: string;
  image?: string;
  to: string;
  kind: "article" | "glas";
  onRequest: boolean;
  /** Effectief prijs-op-aanvraag: alleen als er óók geen ingestelde (override-)prijs is. */
  onRequestEff: boolean;
  hidePrice: boolean;
  hideReviews: boolean;
  vanafCents?: number;
  priceCents: number;
  wasCents: number | null;
  pct: number | null;
  ctaButton?: "product" | "configurator" | "artikel" | "geen";
}

/** Resolveer de promoties die aan `predicate` voldoen (en actief zijn) tot kaart-data. */
export function resolveFeatured(promos: PromoMap, ov: CatalogOverrides, predicate: (p: Promotion) => boolean, max = 6): FeaturedItem[] {
  // Glas-"vanaf" mét admin-marge = exact zoals de soorten-overzichten (soortenWithOverrides).
  const glasVanaf = new Map(soortenWithOverrides(ov).map((s) => [soortSlug(s), s.vanafCents]));
  return Object.values(promos)
    .filter((p) => p.active !== false && predicate(p) && getPromotableItem(p.slug))
    .map((p) => {
      const item = getPromotableItem(p.slug)!;
      // Effectieve prijs (incl. admin-marge/-override) i.p.v. de kale catalogusprijs; zo werkt
      // een marge/afprijzing door én toont een op-aanvraag-artikel mét ingestelde prijs die prijs.
      const effBase = item.kind === "glas" ? item.priceCents : effectivePricePerM2(item.priceCents, item.slug, ov, [item.category]);
      const { priceCents, wasCents } = priceWithSale(effBase, p);
      return {
        slug: p.slug,
        chip: overrideField(p.chip, p.hidePrice ? "" : wasCents != null ? "Actie" : "Uitgelicht"),
        title: overrideField(p.title, item.name),
        subtitle: overrideField(p.subtitle, item.subtitle),
        image: p.heroImageData || item.image,
        to: p.ctaTo || item.to,
        kind: item.kind,
        onRequest: item.onRequest,
        hidePrice: !!p.hidePrice,
        hideReviews: !!p.hideReviews,
        vanafCents: item.kind === "glas"
          ? (glasVanaf.get(item.slug) ?? item.vanafCents)
          : (item.vanafCents != null ? effectivePricePerM2(item.vanafCents, item.slug, ov, [item.category]) : undefined),
        priceCents,
        wasCents,
        onRequestEff: item.onRequest && effBase <= 0,
        pct: wasCents != null ? Math.round((1 - priceCents / wasCents) * 100) : null,
        ctaButton: p.ctaButton,
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title, "nl"))
    .slice(0, max);
}

/**
 * Uitgelicht-rij onder de hero (Coolblue-stijl product-kaarten). Gevuld door de
 * producten/artikelen die in de admin (Acties & uitgelicht) op "Uitgelicht" staan,
 * met hun eigen chip/titel/knop-overrides + van/voor. Leeg = sectie verborgen.
 */
export function FeaturedRow() {
  const t = useT();
  const [pref] = useBtwPreference();
  const cart = useCart();
  const items = resolveFeatured(usePromotions(), useCatalogOverrides(), (p) => placementSet(p).has("row"));
  if (items.length === 0) return null;

  const add = (it: FeaturedItem): void => {
    cart.add({
      kind: "article",
      articleSlug: it.slug,
      quantity: 1,
      priceCents: it.priceCents,
      isCustom: false,
      display: { title: it.title, lines: [it.subtitle].filter(Boolean), imageUrl: it.image },
    });
  };

  return (
    <section aria-label={t("Uitgelicht")} className="mx-auto max-w-[1600px] px-4 pt-6">
      <div className="grid gap-2.5 sm:grid-cols-2 md:gap-3 lg:grid-cols-3">
        {items.map((it) => {
          // Vaste-prijs-artikelen kun je direct bestellen; glas/op-aanvraag niet.
          const canAdd = it.kind === "article" && !it.onRequest && !it.hidePrice && it.priceCents > 0;
          // "geen" = geen knop (de hele kaart blijft klikbaar via de stretched link).
          const showButton = it.ctaButton !== "geen";
          // Welke knop: admin-keuze, anders automatisch. "product" valt terug op
          // "configurator" als het item niet direct bestelbaar is.
          let btn = it.ctaButton && it.ctaButton !== "geen" ? it.ctaButton : (canAdd ? "product" : "configurator");
          if (btn === "product" && !canAdd) btn = "configurator";
          return (
            <div
              key={it.slug}
              className="group relative flex items-stretch justify-between gap-3 rounded-2xl border border-primary-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md"
            >
              {/* Stretched link: hele kaart klikbaar, behalve de knop erboven. */}
              <Link to={it.to} aria-label={t(it.title)} className="absolute inset-0" />
              <div className="pointer-events-none relative flex min-w-0 flex-col">
                {/* Bovenaan: chip → titel → categorie → reviews. */}
                {it.chip && <span className="inline-block w-max rounded bg-accent-700 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">{t(it.chip)}</span>}
                {it.title && <h3 className="mt-3 line-clamp-2 text-lg font-bold leading-tight text-primary-900">{t(it.title)}</h3>}
                {it.subtitle && <p className="mt-1 line-clamp-1 text-sm text-primary-600">{t(it.subtitle)}</p>}
                {!it.hideReviews && <div className="mt-1.5"><ProductRatingBadge slug={it.slug} /></div>}
                {/* Prijs + knop onderaan (mt-auto = uitlijnen over de kaarten). */}
                <div className="mt-auto flex items-center gap-2.5 pt-3 text-sm">
                  <span>
                    {it.hidePrice ? null : it.kind === "glas" && it.vanafCents != null ? (
                      <span className="text-primary-700">{t("vanaf")} <strong className="text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong>/m² <span className="text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span></span>
                    ) : it.vanafCents != null ? (
                      <span className="text-primary-700">{t("vanaf")} <strong className="text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong> <span className="text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span></span>
                    ) : it.onRequestEff ? (
                      <span className="font-semibold text-primary-700">{t("Prijs op aanvraag")}</span>
                    ) : (
                      <span>
                        {it.wasCents != null && <span className="mr-1.5 text-primary-600 line-through">{formatEuro(btwAmount(it.wasCents, pref))}</span>}
                        <strong className={`text-base ${it.wasCents != null ? "text-accent-700" : "text-primary-900"}`}>{formatEuro(btwAmount(it.priceCents, pref))}</strong>
                        <span className="ml-1 text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span>
                      </span>
                    )}
                  </span>
                  {showButton && (btn === "product" ? (
                    <AddToCartButton
                      size="sm"
                      onClick={() => add(it)}
                      ariaLabel={`${t(it.title)} ${t("in winkelwagen")}`}
                      className="pointer-events-auto relative z-10"
                    />
                  ) : (
                    <ConfigureButton
                      to={it.to}
                      tone={btn === "artikel" ? "primary" : "accent"}
                      ariaLabel={btn === "artikel" ? `${t(it.title)} ${t("bekijken")}` : `${t(it.title)} ${t("samenstellen")}`}
                      className="pointer-events-auto relative z-10"
                    />
                  ))}
                </div>
              </div>
              <div className="pointer-events-none relative h-24 w-24 shrink-0 self-center">
                {it.image ? (
                  // Productnaam als alt — zie FeaturedDeals: productfoto's zijn inhoud.
                  <img src={cdnImage(it.image, { w: IMG.card })} alt={t(it.title)} width={IMG.card} height={IMG.card} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-primary-200">{t("geen foto")}</div>
                )}
                {it.pct != null && (
                  <span className="absolute -right-1 -top-1 flex h-9 w-9 items-center justify-center rounded-full bg-accent-700 text-xs font-bold text-white shadow-sm">−{it.pct}%</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
